import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { get, post, del, ApiError } from '../lib/api-client';
import { Alert } from '../components/Alert';

type Tab = 'profile' | 'subscription' | 'product-key' | 'usage' | 'privacy';

interface DailyUsage {
  date: string;
  count: number;
}

interface FeatureMap {
  tier: string;
  features: Record<string, boolean>;
}

interface Subscription {
  tier: string;
  status: string | null;
  currentPeriodEnd: string | null;
  paddleSubscriptionId: string | null;
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'product-key', label: 'Product Key' },
  { key: 'usage', label: 'Usage' },
  { key: 'privacy', label: 'Privacy' },
];

const TIER_COLORS: Record<string, string> = {
  free: 'bg-ll-muted/20 text-ll-muted',
  pro: 'bg-blue-500/20 text-blue-400',
  team: 'bg-purple-500/20 text-purple-400',
  enterprise: 'bg-amber-500/20 text-amber-400',
};

function ProductKeyDisplay({ productKey }: { productKey?: string }) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [currentKey, setCurrentKey] = useState(productKey);
  const [error, setError] = useState<string | null>(null);

  async function handleRegenerate() {
    setRegenerating(true);
    setError(null);
    try {
      const data = await post<{ productKey: string }>('/v1/user/product-key/regenerate');
      setCurrentKey(data.productKey);
      setConfirmRegenerate(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate product key');
    } finally {
      setRegenerating(false);
    }
  }

  function handleCopy() {
    if (!currentKey) return;
    void navigator.clipboard.writeText(currentKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!currentKey) {
    return <p className="text-sm text-ll-muted">No product key available.</p>;
  }

  return (
    <div className="space-y-3">
      {error && <Alert variant="error" message={error} onDismiss={() => setError(null)} />}
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm font-mono text-ll-text select-all break-all">
          {currentKey}
        </code>
        <button
          onClick={handleCopy}
          className="px-3 py-2 rounded-lg border border-ll-border text-sm text-ll-text hover:border-ll-cyan hover:text-ll-cyan transition-all"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {!confirmRegenerate ? (
        <button
          onClick={() => setConfirmRegenerate(true)}
          className="text-xs text-ll-muted hover:text-ll-error transition-colors"
        >
          Regenerate key
        </button>
      ) : (
        <div className="bg-ll-bg border border-ll-error/30 rounded-lg p-3 space-y-2">
          <p className="text-xs text-ll-error">
            Regenerating will invalidate your current product key. All CLI, IDE, and CI/CD
            integrations using the old key will stop working immediately.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => void handleRegenerate()}
              disabled={regenerating}
              className="px-3 py-1.5 rounded-lg bg-ll-error text-white text-xs font-semibold hover:brightness-110 transition-all disabled:opacity-50"
            >
              {regenerating ? 'Regenerating...' : 'Confirm Regenerate'}
            </button>
            <button
              onClick={() => setConfirmRegenerate(false)}
              className="text-xs text-ll-muted hover:text-ll-text transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileTab() {
  const { profile, user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (user?.user_metadata?.display_name) {
      setDisplayName(user.user_metadata.display_name as string);
    } else if (profile?.email) {
      setDisplayName(profile.email.split('@')[0] ?? '');
    }
  }, [user, profile]);

  if (!profile) {
    return <Alert variant="error" message="Unable to load profile. Please try again." />;
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { display_name: displayName },
      });
      if (updateError) {
        setSaveError(updateError.message);
      } else {
        setSaveSuccess(true);
        setEditing(false);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {saveError && (
        <Alert variant="error" message={saveError} onDismiss={() => setSaveError(null)} />
      )}
      {saveSuccess && (
        <Alert
          variant="success"
          message="Profile updated successfully."
          onDismiss={() => setSaveSuccess(false)}
        />
      )}
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Display Name</label>
        {editing ? (
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="flex-1 bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm text-ll-text placeholder-ll-muted/50 focus:outline-none focus:ring-2 focus:ring-ll-cyan"
              placeholder="Your display name"
            />
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-ll-cyan text-ll-bg font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-sm text-ll-muted hover:text-ll-text transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-ll-text">
              {(user?.user_metadata?.display_name as string | undefined) ??
                profile.email.split('@')[0]}
            </p>
            <button
              onClick={() => setEditing(true)}
              className="text-xs text-ll-cyan hover:brightness-110 transition-colors"
            >
              Edit
            </button>
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Email</label>
        <p className="text-ll-text font-mono">{profile.email}</p>
      </div>
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Tier</label>
        <span
          className={`text-sm font-bold uppercase px-2 py-1 rounded ${TIER_COLORS[profile.tier] ?? ''}`}
        >
          {profile.tier}
        </span>
      </div>
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Member Since</label>
        <p className="text-ll-text">{new Date(profile.createdAt).toLocaleDateString()}</p>
      </div>
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">
          Total Analyses
        </label>
        <p className="text-ll-text">{profile.analysisCount.toLocaleString()}</p>
      </div>
      {profile.teamId && (
        <div>
          <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Team</label>
          <p className="text-ll-text font-mono text-sm">{profile.teamId}</p>
        </div>
      )}
    </div>
  );
}

function SubscriptionTab() {
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    get<Subscription>('/v1/billing/subscription')
      .then(setSub)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setSub({
            tier: 'free',
            status: null,
            currentPeriodEnd: null,
            paddleSubscriptionId: null,
          });
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load subscription');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-ll-muted text-sm">Loading...</p>;
  if (error) return <Alert variant="error" message={error} />;
  if (!sub) return null;

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Current Plan</label>
        <span
          className={`text-sm font-bold uppercase px-2 py-1 rounded ${TIER_COLORS[sub.tier] ?? ''}`}
        >
          {sub.tier}
        </span>
      </div>
      {sub.status && (
        <div>
          <label className="block text-xs uppercase font-mono text-ll-muted mb-1">Status</label>
          <p className="text-ll-text capitalize">{sub.status.replace('_', ' ')}</p>
        </div>
      )}
      {sub.currentPeriodEnd && (
        <div>
          <label className="block text-xs uppercase font-mono text-ll-muted mb-1">
            Current Period Ends
          </label>
          <p className="text-ll-text">{new Date(sub.currentPeriodEnd).toLocaleDateString()}</p>
        </div>
      )}
      <div className="flex gap-3">
        {sub.tier === 'free' ? (
          <a
            href="/pricing"
            className="px-4 py-2 rounded-lg bg-ll-cyan text-ll-bg font-semibold text-sm hover:brightness-110 transition-all"
          >
            Upgrade Plan
          </a>
        ) : (
          <button
            onClick={async () => {
              setPortalLoading(true);
              try {
                const data = await post<{ portalUrl: string }>('/v1/billing/portal');
                window.location.href = data.portalUrl;
              } catch {
                setError('Failed to open billing portal');
              } finally {
                setPortalLoading(false);
              }
            }}
            disabled={portalLoading}
            className="px-4 py-2 rounded-lg border border-ll-border text-sm text-ll-text hover:border-ll-cyan hover:text-ll-cyan transition-all disabled:opacity-50"
          >
            {portalLoading ? 'Loading...' : 'Manage Subscription'}
          </button>
        )}
      </div>
    </div>
  );
}

function ProductKeyTab() {
  const { profile } = useAuth();

  if (!profile) return <p className="text-ll-muted text-sm">Loading...</p>;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-ll-muted">
          Use this key to authenticate the CLI, IDE plugins, and CI/CD integrations.
        </p>
      </div>
      <ProductKeyDisplay productKey={profile.productKey} />
    </div>
  );
}

function UsageTab() {
  const [usage, setUsage] = useState<DailyUsage[]>([]);
  const [features, setFeatures] = useState<FeatureMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      get<{ daily: DailyUsage[] }>('/v1/user/usage'),
      get<FeatureMap>('/v1/user/features'),
    ])
      .then(([usageData, featureData]) => {
        setUsage(usageData.daily);
        setFeatures(featureData);
      })
      .catch((err: unknown) => {
        if (err instanceof TypeError && err.message.includes('fetch')) {
          setError('Unable to connect to the API. Please try again later.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load usage data');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-ll-muted text-sm">Loading...</p>;
  if (error) return <Alert variant="error" message={error} />;

  const totalAnalyses = usage.reduce((sum, d) => sum + d.count, 0);
  const maxCount = Math.max(...usage.map((d) => d.count), 1);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-ll-text mb-1">Last 30 Days</h3>
        <p className="text-2xl font-bold text-ll-cyan">{totalAnalyses} analyses</p>
      </div>

      {usage.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ll-text mb-3">Daily Activity</h3>
          <div className="flex items-end gap-[2px] h-24">
            {usage.map((d) => (
              <div
                key={d.date}
                className="flex-1 bg-ll-cyan/60 rounded-t hover:bg-ll-cyan transition-colors"
                style={{
                  height: `${(d.count / maxCount) * 100}%`,
                  minHeight: d.count > 0 ? '4px' : '0',
                }}
                title={`${d.date}: ${d.count} analyses`}
              />
            ))}
          </div>
        </div>
      )}

      {features && (
        <div>
          <h3 className="text-sm font-semibold text-ll-text mb-3">Feature Access</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(features.features).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center gap-2 text-sm">
                <span className={enabled ? 'text-ll-success' : 'text-ll-muted'}>
                  {enabled ? '\u2713' : '\u2717'}
                </span>
                <span className={enabled ? 'text-ll-text' : 'text-ll-muted'}>
                  {feature.replace(/-/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PrivacyTab() {
  const { signOut } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportData() {
    setExporting(true);
    setError(null);
    try {
      const data = await get<Record<string, unknown>>('/v1/user/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'layerlint-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  async function deleteAccount() {
    setDeleting(true);
    setError(null);
    try {
      await del('/v1/user/account');
      await signOut();
      window.location.href = '/sign-in';
    } catch (err: unknown) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Please sign in again recently to delete your account (re-authentication required).'
          : err instanceof Error
            ? err.message
            : 'Deletion failed',
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-8">
      {error && <Alert variant="error" message={error} onDismiss={() => setError(null)} />}

      <div>
        <h3 className="text-sm font-semibold text-ll-text mb-2">Export Your Data</h3>
        <p className="text-sm text-ll-muted mb-3">
          Download a JSON export of your profile, analysis history, and fix acceptance data.
        </p>
        <button
          onClick={() => void exportData()}
          disabled={exporting}
          className="px-4 py-2 rounded-lg border border-ll-border text-sm text-ll-text hover:border-ll-cyan hover:text-ll-cyan transition-all disabled:opacity-50"
        >
          {exporting ? 'Exporting...' : 'Export Data'}
        </button>
      </div>

      <div className="border-t border-ll-border pt-8">
        <h3 className="text-sm font-semibold text-ll-error mb-2">Delete Account</h3>
        <p className="text-sm text-ll-muted mb-3">
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="px-4 py-2 rounded-lg border border-ll-error/50 text-sm text-ll-error hover:bg-ll-error/10 transition-all"
          >
            Delete Account
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={() => void deleteAccount()}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-ll-error text-white text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Confirm Delete'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-sm text-ll-muted hover:text-ll-text transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Account() {
  const [tab, setTab] = useState<Tab>('profile');

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <h1 className="text-3xl font-bold text-ll-text mb-8">Account</h1>

      <div className="flex gap-1 mb-8 border-b border-ll-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'text-ll-cyan border-ll-cyan'
                : 'text-ll-muted border-transparent hover:text-ll-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-ll-surface border border-ll-border rounded-2xl p-6">
        {tab === 'profile' && <ProfileTab />}
        {tab === 'subscription' && <SubscriptionTab />}
        {tab === 'product-key' && <ProductKeyTab />}
        {tab === 'usage' && <UsageTab />}
        {tab === 'privacy' && <PrivacyTab />}
      </div>
    </div>
  );
}
